import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import useApi from "../../hook/useAPI";
import { SUBSCRIPTION } from "../../utils/const";
import {
  descriptionToFormValue,
  formatCurrencyAmount,
  formatPlanPriceSummary,
  getPlanCatalogType,
  getPlanCatalogTypeLabel,
} from "../../utils/planCatalogDisplay";
import "./SubscriptionManagement.scss";

const displayValue = (value) => value || "-";
const formatPrice = (value) => formatCurrencyAmount(value);

const ChangeValue = ({ label, previous, next, isPrice = false }) => (
  <div className="stripe-change-row">
    <span className="stripe-change-label">{label}</span>
    <div className="stripe-change-values">
      <code>{isPrice ? formatPrice(previous) : displayValue(previous)}</code>
      <span className="stripe-change-arrow">to</span>
      <code className="stripe-change-next">{isPrice ? formatPrice(next) : displayValue(next)}</code>
    </div>
  </div>
);

const SubscriptionDetail = ({ show, handleClose, subscriptionData }) => {
  const { getData } = useApi();
  const [activeTab, setActiveTab] = useState("details");
  const [changeRecords, setChangeRecords] = useState([]);

  useEffect(() => {
    const fetchChangeRecords = async () => {
      if (!show || !subscriptionData?._id) return;
      try {
        const response = await getData(
          `${SUBSCRIPTION.STRIPE_CHANGE_RECORDS}?planId=${subscriptionData._id}&limit=50`
        );
        setChangeRecords(response?.data || []);
      } catch (error) {
        console.error("Unable to fetch Stripe plan change records:", error);
      }
    };

    fetchChangeRecords();
  }, [getData, show, subscriptionData?._id]);

  const handleModalClose = () => {
    setActiveTab("details");
    setChangeRecords([]);
    handleClose();
  };

  const {
    planName,
    description,
    price,
    monthlyPrice,
    yearlyPrice,
    currency = "usd",
  } = subscriptionData || {};
  const catalogType = subscriptionData?.planCatalogType || getPlanCatalogType(subscriptionData);
  const catalogTypeLabel = subscriptionData?.planCatalogTypeLabel || getPlanCatalogTypeLabel(subscriptionData);
  const descriptionLines = descriptionToFormValue(description).split(/\r?\n/).filter(Boolean);

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      size="xl"
      className="bs_common_modal add-device-modal user-details-modal subscription-etail-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Plan catalog detail</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="dialog-content-body">
          <div className="user-details-tabs">
            <button
              type="button"
              className={`user-details-tab ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </button>
            <button
              type="button"
              className={`user-details-tab ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
            >
              Price Change History
            </button>
          </div>

          {activeTab === "details" && (
            <div className="stripe-plan-overview">
              <div className="stripe-overview-hero">
                <div>
                  <span className="stripe-eyebrow">Current catalog mapping</span>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h4 className="mb-0">{planName}</h4>
                    <span className={`plan-catalog-type-badge ${catalogType}`}>{catalogTypeLabel}</span>
                  </div>
                  {descriptionLines.length > 0 ? (
                    <ul className="plan-catalog-feature-list">
                      {descriptionLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>-</p>
                  )}
                </div>
                <div className="stripe-plan-price">
                  <span>{formatCurrencyAmount(monthlyPrice ?? price)} / month</span>
                  <small>
                    {yearlyPrice
                      ? `${formatCurrencyAmount(yearlyPrice)} / year`
                      : "Yearly amount not set"}
                  </small>
                  <small>{formatPlanPriceSummary(subscriptionData)}</small>
                </div>
              </div>
              <div className="stripe-id-grid">
                {[
                  ["Stripe Product ID", subscriptionData?.stripeProductId],
                  ["Monthly Price ID", subscriptionData?.stripeMonthlyPriceId],
                  ["Yearly Price ID", subscriptionData?.stripeYearlyPriceId],
                  ["Legacy Price ID", subscriptionData?.stripePriceId],
                ].map(([label, value]) => (
                  <div className="stripe-id-card" key={label}>
                    <span>{label}</span>
                    <code>{displayValue(value)}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <div className="stripe-history-list">
              {changeRecords.length === 0 && (
                <div className="stripe-empty-state">
                  <strong>No Stripe pricing changes recorded</strong>
                  <span>Future admin catalog updates will appear here.</span>
                </div>
              )}
              {changeRecords.map((record) => (
                <article key={record._id} className="stripe-history-card">
                  <div className="stripe-history-header">
                    <div>
                      <span className="stripe-history-date">{new Date(record.createdAt).toLocaleString()}</span>
                      <strong>{record.actor?.email || record.actor?.adminId?.email || "Admin"}</strong>
                    </div>
                    <div className="stripe-history-badges">
                      <span>{record.affectedUserCount || 0} affected user(s)</span>
                      <span>{(record.changedFields || []).length} field(s) changed</span>
                    </div>
                  </div>
                  <div className="stripe-change-list">
                    <ChangeValue label="Display price" previous={record.previous?.price} next={record.next?.price} isPrice />
                    <ChangeValue label="Monthly amount" previous={record.previous?.monthlyPrice} next={record.next?.monthlyPrice} isPrice />
                    <ChangeValue label="Yearly amount" previous={record.previous?.yearlyPrice} next={record.next?.yearlyPrice} isPrice />
                    <ChangeValue label="Stripe product" previous={record.previous?.stripeProductId} next={record.next?.stripeProductId} />
                    <ChangeValue label="Monthly price" previous={record.previous?.stripeMonthlyPriceId} next={record.next?.stripeMonthlyPriceId} />
                    <ChangeValue label="Yearly price" previous={record.previous?.stripeYearlyPriceId} next={record.next?.stripeYearlyPriceId} />
                  </div>
                  {record.note && <div className="stripe-history-note">{record.note}</div>}
                  {(record.enrolledUsers || []).length > 0 && (
                        <details className="stripe-enrolled-users">
                          <summary>View enrolled user snapshot</summary>
                          <div className="stripe-user-list">
                          {(record.enrolledUsers || []).map((entry, index) => (
                            <div className="stripe-user-row" key={`${entry.userId?._id || entry.userId}-${index}`}>
                              <div>
                                <strong>{entry.userId?.email || entry.userId}</strong>
                                <span>{entry.enrollmentType?.replace("_", " ")} | {entry.billingPeriod}</span>
                              </div>
                              <code>{displayValue(entry.previousStripePriceId)}</code>
                            </div>
                          ))}
                          </div>
                        </details>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SubscriptionDetail;
